/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.auth

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.feature.auth.generated.resources.Res
import studio.oddpods.cauce.feature.auth.generated.resources.landing_error_invalid_email
import studio.oddpods.cauce.feature.auth.generated.resources.landing_error_required
import studio.oddpods.cauce.feature.auth.generated.resources.landing_error_send_failed

data class LandingState(
    val email: String = "",
    val isLoading: Boolean = false,
    val error: UiText? = null,
)

sealed class LandingEvent {
    data class OtpSent(val email: String) : LandingEvent()
}

sealed class LandingAction {
    data class EmailChanged(val email: String) : LandingAction()
    data object ContinueClicked : LandingAction()
}

class LandingViewModel(
    private val authService: AuthService,
) : BaseViewModel<LandingState, LandingEvent, LandingAction>(LandingState()) {

    override fun handleAction(action: LandingAction) {
        when (action) {
            is LandingAction.EmailChanged -> {
                mutableStateFlow.value = state.copy(email = action.email, error = null)
            }
            LandingAction.ContinueClicked -> handleContinue()
        }
    }

    private fun handleContinue() {
        val normalized = state.email.trim().lowercase()

        if (normalized.isBlank()) {
            mutableStateFlow.value = state.copy(
                error = UiText.Resource(Res.string.landing_error_required),
            )
            return
        }
        if (!EMAIL_REGEX.matches(normalized)) {
            mutableStateFlow.value = state.copy(
                error = UiText.Resource(Res.string.landing_error_invalid_email),
            )
            return
        }

        mutableStateFlow.value = state.copy(isLoading = true, error = null)

        viewModelScope.launch {
            authService.sendOtp(normalized).fold(
                onSuccess = {
                    mutableStateFlow.value = state.copy(isLoading = false, email = normalized)
                    sendEvent(LandingEvent.OtpSent(normalized))
                },
                onFailure = { error ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        error = error.message?.let { UiText.Dynamic(it) }
                            ?: UiText.Resource(Res.string.landing_error_send_failed),
                    )
                },
            )
        }
    }

    private companion object {
        private val EMAIL_REGEX = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")
    }
}
